import React, { useEffect, useId, useMemo, useRef } from "react";
import { init, dispose, registerOverlay } from "klinecharts";

let _overlaysRegistered = false;

function registerEducaOverlays() {
  if (_overlaysRegistered) return;
  _overlaysRegistered = true;
  // hline + label
  registerOverlay({
    name: "educaHLine",
    totalStep: 2,
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      if (!coordinates?.[0]) return [];
      const y = coordinates[0].y;
      const styles = overlay.styles || {};
      const color = styles.line?.color || "#d8a868";
      const dashed = styles.line?.style === "dashed";
      const label = overlay.extendData?.label || "";
      return [
        {
          type: "line",
          attrs: { coordinates: [{ x: 0, y }, { x: bounding.width, y }] },
          styles: { color, style: dashed ? "dashed" : "solid", dashedValue: [3, 3], size: 1 },
        },
        label && {
          type: "text",
          attrs: { x: bounding.width - 6, y: y - 14, text: label, baseline: "bottom", align: "right" },
          styles: { color, family: '"JetBrains Mono", monospace', size: 9, weight: "500" },
        },
      ].filter(Boolean);
    },
  });

  // shaded zone (band between two prices)
  registerOverlay({
    name: "educaZone",
    totalStep: 3,
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      if (!coordinates?.[0] || !coordinates?.[1]) return [];
      const y1 = Math.min(coordinates[0].y, coordinates[1].y);
      const y2 = Math.max(coordinates[0].y, coordinates[1].y);
      const styles = overlay.styles || {};
      const color = styles.polygon?.color || "rgba(216,196,160,0.18)";
      const stroke = styles.polygon?.borderColor || "rgba(216,196,160,0.55)";
      const label = overlay.extendData?.label || "";
      return [
        {
          type: "polygon",
          attrs: {
            coordinates: [
              { x: 0, y: y1 },
              { x: bounding.width, y: y1 },
              { x: bounding.width, y: y2 },
              { x: 0, y: y2 },
            ],
          },
          styles: { style: "solid_stroke_fill", color, borderColor: stroke, borderSize: 1 },
        },
        label && {
          type: "text",
          attrs: { x: 8, y: y1 - 12, text: label, baseline: "bottom", align: "left" },
          styles: { color: stroke, family: '"JetBrains Mono", monospace', size: 9, weight: "500" },
        },
      ].filter(Boolean);
    },
  });

  // event marker (vertical line + label)
  registerOverlay({
    name: "educaMarker",
    totalStep: 2,
    createPointFigures: ({ coordinates, bounding, overlay }) => {
      if (!coordinates?.[0]) return [];
      const { x, y } = coordinates[0];
      const styles = overlay.styles || {};
      const color = styles.line?.color || "#5fd6c1";
      const label = overlay.extendData?.label || "";
      return [
        {
          type: "line",
          attrs: { coordinates: [{ x, y: 0 }, { x, y: bounding.height }] },
          styles: { color, style: "dashed", dashedValue: [2, 3], size: 1 },
        },
        {
          type: "circle",
          attrs: { x, y, r: 4 },
          styles: { color, style: "fill" },
        },
        label && {
          type: "text",
          attrs: { x: x + 6, y: y - 8, text: label, baseline: "bottom" },
          styles: { color, family: '"JetBrains Mono", monospace', size: 9, weight: "500" },
        },
      ].filter(Boolean);
    },
  });
}

const SAMPLE_URL = "/data/ohlcv-sample.json";
let _samplesPromise = null;

function loadSamples() {
  if (!_samplesPromise) {
    _samplesPromise = fetch(SAMPLE_URL)
      .then((r) => {
        if (!r.ok) throw new Error("ohlcv-sample.json unavailable");
        return r.json();
      })
      .catch(() => ({ series: {} }));
  }
  return _samplesPromise;
}

const SERIES_BY_TOPIC = {
  // best-effort mapping; falls back to didactic-clean
  "tendencias": "trend-uptrend",
  "soportes_resistencias": "support-zone",
  "soportes_resistencias_rupturas": "false-breakout",
  "rupturas": "false-breakout",
  "momentum_rsi": "rsi-overbought",
  "momentum": "rsi-overbought",
  "volatilidad": "volatility-expansion",
  "multi_timeframe": "multi-timeframe-daily",
};

/**
 * Drop-in replacement for the placeholder `MChart` used by the Materia screens.
 * Renders a real klinecharts candlestick using deterministic synthetic OHLCV
 * data, with declarative overlays (zone, hline, marker) translated from the
 * legacy `overlays` prop. Keeps the same width/height/caption signature.
 */
export function MChartReal({
  width = 600,
  height = 280,
  caption = "CHART · MOCK · 1D",
  overlays = [],
  series,
  indicators = [],
  decoration = "materia",
  showVolume = true,
}) {
  const ref = useRef(null);
  const chartRef = useRef(null);
  const id = useId().replace(/:/g, "");

  const seriesKey = useMemo(() => {
    if (series) return series;
    // try to infer from caption "CHART · XYZ · 1D" → not enough; default
    return "trend-uptrend";
  }, [series]);

  useEffect(() => {
    registerEducaOverlays();
    const chart = init(ref.current, {
      styles: themeStyles(decoration, showVolume),
      timezone: "UTC",
    });
    chartRef.current = chart;
    return () => {
      dispose(ref.current);
      chartRef.current = null;
    };
  }, [decoration, showVolume]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const data = await loadSamples();
      const arr = data.series?.[seriesKey] ?? data.series?.["didactic-clean"] ?? [];
      if (cancelled || !chartRef.current) return;
      chartRef.current.applyNewData(arr);

      // remove any pre-existing overlays
      chartRef.current.removeOverlay();

      // indicators
      for (const ind of indicators) {
        chartRef.current.createIndicator(ind, false, { id: `ind-${ind}` });
      }

      // overlays translated from legacy MChart API
      // We map percentage-based coordinates into actual price/time space:
      const lastIdx = arr.length - 1;
      const lows = arr.map((d) => d.low);
      const highs = arr.map((d) => d.high);
      const priceMin = Math.min(...lows);
      const priceMax = Math.max(...highs);
      const pctToPrice = (pct) => priceMax - ((priceMax - priceMin) * pct) / 100;
      const pctToIdx = (pct) => Math.max(0, Math.min(lastIdx, Math.round((pct / 100) * lastIdx)));

      for (const ov of overlays) {
        if (!ov) continue;
        try {
          if (ov.type === "zone") {
            chartRef.current.createOverlay({
              name: "educaZone",
              extendData: { label: ov.label || "ZONA" },
              points: [
                { timestamp: arr[0].timestamp, value: pctToPrice(ov.topPct ?? 30) },
                { timestamp: arr[lastIdx].timestamp, value: pctToPrice(ov.bottomPct ?? 60) },
              ],
              styles: {
                polygon: {
                  color: ov.color === "copper" ? "rgba(216,196,160,0.18)" : ov.color || "rgba(95,214,193,0.16)",
                  borderColor: ov.color === "copper" ? "rgba(216,196,160,0.55)" : "rgba(95,214,193,0.55)",
                },
              },
            });
          } else if (ov.type === "hline") {
            chartRef.current.createOverlay({
              name: "educaHLine",
              extendData: { label: ov.label || "" },
              points: [{ timestamp: arr[0].timestamp, value: pctToPrice(ov.yPct ?? 50) }],
              styles: {
                line: {
                  color: ov.color || "rgba(216,196,160,0.55)",
                  style: ov.dashed ? "dashed" : "solid",
                },
              },
            });
          } else if (ov.type === "marker") {
            chartRef.current.createOverlay({
              name: "educaMarker",
              extendData: { label: ov.label || "" },
              points: [
                {
                  timestamp: arr[pctToIdx(ov.xPct ?? 50)].timestamp,
                  value: pctToPrice(ov.yPct ?? 50),
                },
              ],
              styles: {
                line: {
                  color:
                    ov.kind === "decision"
                      ? "#5fd6c1"
                      : ov.color || "#d8a868",
                },
              },
            });
          }
        } catch (err) {
          // overlay registration may fail in dev hot-reload; ignore
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [seriesKey, JSON.stringify(overlays), JSON.stringify(indicators)]);

  return (
    <figure
      style={{
        margin: 0,
        position: "relative",
        width,
        height,
        background: decoration === "materia" ? "#23170d" : "#0e1a36",
        borderRadius: 8,
        overflow: "hidden",
        border: decoration === "materia" ? "1px solid rgba(216,196,160,0.18)" : "1px solid rgba(127,182,255,0.15)",
      }}
    >
      <div ref={ref} id={`chart-${id}`} style={{ width: "100%", height: "100%" }} />
      <figcaption
        className="mono"
        style={{
          position: "absolute",
          top: 6,
          left: 8,
          fontSize: 10,
          letterSpacing: "0.08em",
          color: decoration === "materia" ? "rgba(216,196,160,0.7)" : "rgba(168,185,217,0.65)",
          textTransform: "uppercase",
          pointerEvents: "none",
        }}
      >
        {caption}
      </figcaption>
    </figure>
  );
}

function themeStyles(decoration, showVolume) {
  const materia = decoration === "materia";
  const grid = materia ? "rgba(216,196,160,0.08)" : "rgba(168,185,217,0.10)";
  const axisColor = materia ? "rgba(216,196,160,0.45)" : "rgba(168,185,217,0.55)";
  const text = materia ? "#e8d8b8" : "#dbe4f5";
  const bull = materia ? "#a6c576" : "#5fd6c1";
  const bear = materia ? "#c7593a" : "#ff7a8c";
  return {
    grid: {
      show: true,
      horizontal: { show: true, size: 1, color: grid, style: "dashed", dashedValue: [2, 3] },
      vertical: { show: true, size: 1, color: grid, style: "dashed", dashedValue: [2, 3] },
    },
    candle: {
      type: "candle_solid",
      bar: { upColor: bull, downColor: bear, noChangeColor: axisColor, upBorderColor: bull, downBorderColor: bear, upWickColor: bull, downWickColor: bear },
      priceMark: { show: false },
      tooltip: { showRule: "follow_cross" },
    },
    indicator: {
      tooltip: { showRule: "follow_cross" },
      lines: [
        { size: 1, color: materia ? "#d8a868" : "#7fb6ff" },
        { size: 1, color: materia ? "#c97a4b" : "#5fd6c1" },
      ],
    },
    xAxis: {
      show: true,
      axisLine: { color: axisColor, size: 1 },
      tickText: { color: text, size: 9, family: '"JetBrains Mono", monospace' },
      tickLine: { color: axisColor, size: 1, length: 3 },
    },
    yAxis: {
      show: true,
      position: "right",
      axisLine: { color: axisColor, size: 1 },
      tickText: { color: text, size: 9, family: '"JetBrains Mono", monospace' },
      tickLine: { color: axisColor, size: 1, length: 3 },
    },
    crosshair: {
      horizontal: {
        line: { color: axisColor, style: "dashed", dashedValue: [2, 3], size: 1 },
        text: { color: text, backgroundColor: materia ? "#3a2a1a" : "#11203f", family: '"JetBrains Mono", monospace', size: 10 },
      },
      vertical: {
        line: { color: axisColor, style: "dashed", dashedValue: [2, 3], size: 1 },
        text: { color: text, backgroundColor: materia ? "#3a2a1a" : "#11203f", family: '"JetBrains Mono", monospace', size: 10 },
      },
    },
  };
}
